import { isEmpty, groupBy } from 'lodash';
import * as URI from 'urijs';
import { QueryDataMap } from 'urijs';
import 'urijs/src/URITemplate';
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

import { getUbiEventsEndpoint } from '@sitegen/common/utils/ubi-services';
import {
    IBetaAPIError,
    IBetaCustomFieldsResponse,
    IBetaError,
    IBetaFriend,
    IBetaFriendsResponse,
    IBetaKey,
    IConfigureBetaManagerServiceOptions,
    IGetBetaCustomFieldsOptions,
    IGetFriendListForBetaOptions,
    IGetPlatformPlayerGroupsOptions,
    IGetPlayerBetaKeysResponse,
    IGetPlayerProfileOptions,
    IGetPlayerProfileResponse,
    IInviteFriendToBetaOptions,
    IPlatformPlayerGroupResponse,
    IPlatformPlayerGroups,
    IPlatformPlayerGroupsResponse,
    IPlayerBetaFriendsOptions,
    IPlayerGroupResponse,
    IPlayerProfile,
    IRegisterForBetaRequest,
    IRegisterPlayerForBetaOptions,
    IRegisterPlayerInBetaGroupOptions,
    IGetPlatformPlayerGroupsFromResponse,
    IPlayerGroup
} from './beta-manager-api.types';
import {
    BETA_PLATFORM_ID_TO_PLATFORM,
    CUSTOM_FIELDS_URL,
    NDA_STATUS,
    PLATFORM_PLAYER_GROUPS_URL,
    PLATFORM_TO_BETA_PLATFORM_ID,
    PLAYER_BETA_GROUP_URL,
    PLAYER_BETA_KEYS_URL,
    PLAYER_FRIENDS_STATUS_URL,
    PLAYER_INVITE_FRIEND_URL,
    PLAYER_PROFILE_URL,
    SITEGEN_CTA_VALUE,
    REGION_MATCH_ONLY_PLATFORMS
} from './beta-manager-api.constants';
import { IBetaPhaseSystemFieldValue } from '../beta-manager-store';
import { IFieldRenderParams } from 'modules/additional-info-common/fields/fields.types';
import { betaCustomFieldsAdapter } from './beta-custom-fields.adapter';
import { WIDGET_NAME } from '@sitegen/common/constants';

/**
 * Collection of requests to "beta.ubi.com"
 */
export class BetaManagerAPIService {
    private static AXIOS_INSTANCE: AxiosInstance;

    /** Provide headers and other options to each request */
    public static configure(options: IConfigureBetaManagerServiceOptions): void {
        const headers: object = {
            'Ubi-AppId': options.appId,
            'Ubi-GenomeId': options.genomeId,
            'Ubi-Ticket': options.ticket,
            'Ubi-SessionId': options.sessionId
        };

        this.AXIOS_INSTANCE = axios.create({ headers });
    }

    public static configureFakeAPI(): Promise<void> {
        /* eslint-disable-next-line*/
        return import('./__mocks__/beta-manager-api.service.mocks').then((module: any): void => {
            this.getFriendListForBeta = module.getFriendListForBeta;
            this.getPlayerBetaKeys = module.getPlayerBetaKeys;
            this.inviteFriendToBeta = module.inviteFriendToBeta;
            this.getInvitedFriends = module.getInvitedFriends;
        });
    }

    /** Get information about player registration in given beta phase */
    public static async getPlayerProfile(options: IGetPlayerProfileOptions): Promise<IPlayerProfile> {
        const url: string = this.getPlayerProfileURL(options).href();
        const response: IGetPlayerProfileResponse = await this.request({ url, method: 'GET' });

        return this.getPlayerProfileFromResponse(response);
    }

    public static async getCustomFields(options: IGetBetaCustomFieldsOptions): Promise<IFieldRenderParams[]> {
        const url: string = this.getCustomFieldsURL(options).href();
        const response: IBetaCustomFieldsResponse = await this.request({ url, method: 'GET' });
        return this.getCustomFieldsFromResponse(response);
    }

    public static async getPlatformPlayerGroups(options: IGetPlatformPlayerGroupsOptions): Promise<IPlatformPlayerGroups> {
        const url: URI = this.getPlatformPlayerGroupsURL(options);
        const response: IPlatformPlayerGroupsResponse = await this.request({ url: url.href(), method: 'GET' });

        return this.getPlatformPlayerGroupsFromResponse({
            groups: response.PlayerGroups,
            tag: options.tag,
            widgetName: options.widgetName
        });
    }

    /** Try to sign up player for beta on given platform. Response should contain new information about player groups */
    public static async setPlayerBetaProfile(options: IRegisterPlayerForBetaOptions): Promise<IPlayerProfile> {
        const url: URI = this.getPlayerProfileURL(options)
            .query({ platformId: PLATFORM_TO_BETA_PLATFORM_ID[options.platform].toString() });

        if (options.newPlatform && options.alreadyRegisteredForBeta && options.platform !== options.newPlatform) {
            url.addQuery({ newPlatformId: PLATFORM_TO_BETA_PLATFORM_ID[options.newPlatform] });
        }

        /* Send PUT request with updated NDA value or new platform if user is already registered */
        const method: string = options.alreadyRegisteredForBeta ? 'PUT' : 'POST';
        const data: object = this.getRegisterForBetaInfo(options);

        const response: IGetPlayerProfileResponse = await this.request({ url: url.href(), method, data });
        return this.getPlayerProfileFromResponse(response);
    }

    /** Register a new player for a given beta/player group or update it if user is already registered */
    public static async setPlayerActiveBetaGroup(options: IRegisterPlayerInBetaGroupOptions): Promise<IPlayerProfile> {
        const url: URI = this.setPlayerBetaGroupURL(options);

        const method: string = options.alreadyRegisteredForBeta ? 'PUT' : 'POST';
        const data: object = this.getRegisterForBetaInfo(options);

        const response: IGetPlayerProfileResponse = await this.request({ url: url.href(), method, data });
        return this.getPlayerProfileFromResponse(response);
    }

    /** Get list of friend objects already invited to beta */
    public static async getFriendListForBeta(options: IGetFriendListForBetaOptions): Promise<IBetaFriend[]> {
        const host: string = getUbiEventsEndpoint(options.env);
        const values: object = { code: options.code, phaseId: options.phaseId.toString() };
        const template: string = URI.expand!(PLAYER_FRIENDS_STATUS_URL, values).toString();
        const url: string = new URI(host).segment(template).href();

        const response: IBetaFriendsResponse = await this.request({ url, method: 'POST', data: options.friendIdList });
        return response.Friends;
    }

    /** Invite friends to Beta for given platform (platform ID is required for this method) */
    public static async inviteFriendToBeta(options: IInviteFriendToBetaOptions): Promise<object> {
        const url: URI = this.getFriendListForBetaURL(options);
        const data: IBetaFriendsResponse = { Friends: [] };
        for (const friend of options.friends) {
            data.Friends.push({ FriendId: friend });
        }

        return this.request({ url: url.href(), method: 'POST', data });
    }

    /** Get list of friends invited by the current player */
    public static async getInvitedFriends(options: IGetPlayerProfileOptions): Promise<IBetaFriend[]> {
        const url: URI = this.getFriendListForBetaURL(options);
        const response: IBetaFriendsResponse = await this.request({ url: url.href(), method: 'GET' });
        return response.Friends;
    }

    /** Get beta keys available for player, for all platforms */
    public static async getPlayerBetaKeys(options: IGetPlayerProfileOptions): Promise<IBetaKey[]> {
        const host: string = getUbiEventsEndpoint(options.env);
        const values: object = {
            code: options.code,
            phaseId: options.phaseId.toString(),
            userId: options.userId
        };
        const template: string = URI.expand!(PLAYER_BETA_KEYS_URL, values).toString();
        const url: string = new URI(host).segment(template).href();

        const response: IGetPlayerBetaKeysResponse = await this.request({ url, method: 'GET' });
        const result: IBetaKey[] = [];

        for (const key of response.PlayerKeys) {
            result.push({
                key: key.PlayerKey,
                platformId: BETA_PLATFORM_ID_TO_PLATFORM[key.PlatformId],
                playerGroupId: key.PlayerGroupId
            });
        }

        return result;
    }

    /** Data that is send to RS API when registering user */
    private static getRegisterForBetaInfo(options: {
        isNDASignedByUser: boolean,
        ubiMail?: string,
        customFieldValues?: IBetaPhaseSystemFieldValue[],
        cta: string | null
    }): IRegisterForBetaRequest {
        const data: IRegisterForBetaRequest = {
            PlayerSystemInfo: {
                Components: {
                    CTA: options.cta || SITEGEN_CTA_VALUE
                }
            }
        };

        if (options.isNDASignedByUser) {
            data.PlayerSystemInfo.Components.NDA = 'true';
        }

        if (options.ubiMail) {
            data.PlayerSystemInfo.Components.Ubimail = options.ubiMail;
        }

        if (options.customFieldValues && !isEmpty(options.customFieldValues)) {
            options.customFieldValues.forEach(field => {
                data.PlayerSystemInfo.Components[field.key] = field.value;
            });
        }

        return data;
    }

    /** Get URL for retrieving player profile as well as sign player for beta */
    private static getPlayerProfileURL(options: IGetPlayerProfileOptions): URI {
        const host: string = getUbiEventsEndpoint(options.env);
        const values: object = { code: options.code, phaseId: options.phaseId.toString(), userId: options.userId };
        const template: string = URI.expand!(PLAYER_PROFILE_URL, values).toString();
        return new URI(host).segment(template);
    }

    private static getCustomFieldsURL(options: IGetBetaCustomFieldsOptions): URI {
        const host: string = getUbiEventsEndpoint(options.env);
        const values: object = { code: options.code, phaseId: options.phaseId.toString() };
        const template: string = URI.expand!(CUSTOM_FIELDS_URL, values).toString();
        return new URI(host).segment(template);
    }

    /** Get URL for retrieving or updating friend invitations to beta */
    private static getFriendListForBetaURL(options: IPlayerBetaFriendsOptions): URI {
        const host: string = getUbiEventsEndpoint(options.env);
        const values: object = { code: options.code, phaseId: options.phaseId.toString(), userId: options.userId };
        const template: string = URI.expand!(PLAYER_INVITE_FRIEND_URL, values).toString();

        const query: QueryDataMap = (options.platform) ? { platformId: PLATFORM_TO_BETA_PLATFORM_ID[options.platform].toString() } : {};
        return new URI(host).segment(template).query(query);
    }

    private static setPlayerBetaGroupURL(options: IRegisterPlayerInBetaGroupOptions) {
        const host: string = getUbiEventsEndpoint(options.env);
        const values: object = {
            code: options.code,
            phaseId: options.phaseId.toString(),
            userId: options.userId,
            playerGroupId: options.playerGroupId
        };
        const template: string = URI.expand!(PLAYER_BETA_GROUP_URL, values).toString();
        return new URI(host).segment(template);
    }

    private static getPlatformPlayerGroupsURL(options: IGetPlatformPlayerGroupsOptions) {
        const host: string = getUbiEventsEndpoint(options.env);
        const values: object = { code: options.code, phaseId: options.phaseId.toString() };
        const template: string = URI.expand!(PLATFORM_PLAYER_GROUPS_URL, values).toString();
        return new URI(host).segment(template);
    }

    private static getCustomFieldsFromResponse(response: IBetaCustomFieldsResponse): IFieldRenderParams[] {
        if (response.SystemFields && !isEmpty(response.SystemFields)) {
            return betaCustomFieldsAdapter(response.SystemFields);
        }

        return [];
    }

    /** Convert response to object used in application */
    private static getPlayerProfileFromResponse(response: IGetPlayerProfileResponse): IPlayerProfile {
        const groups: IPlayerGroup[] = [];
        for (const group of response.PlayerGroups) {
            groups.push({
                id: group.PlayerGroupId,
                approved: group.IsApproved,
                platform: BETA_PLATFORM_ID_TO_PLATFORM[group.PlatformId],
                sourceIdList: group.PlayerSourceIds
            });
        }

        return {
            userId: response.UserId,
            banned: response.BannedInBeta,
            groups,
            nda: this.getNDAStatus(response),
            cta: this.getCTA(response)
        };
    }

    public static getPlatformPlayerGroupsFromResponse: IGetPlatformPlayerGroupsFromResponse = ({
        groups,
        tag: tagFromConfig,
        widgetName
    }) => {
        let platforms: IPlatformPlayerGroups = {};
        /**
         * A group is eligible if, for a specific Beta:
         * - only one group exists for a given platform
         * OR
         * - only one group with a "regionMatch": true if multiple groups exists for a same platform.
         * The "regionMatch" is a exclusive for consoles (PS4, PS5, XSX, XONE), all other platforms normally return an empty value.
         *
         * If no or multiple groups exists for a given platform (without exactly one of these groups having a
         * regionMatch: true property), it is considered this Beta has no eligible group for this platform.
         *
         * If, from the admin, a tag has been configured for the auto-push feature, the group eligibility criteria changes :
         * it is no longer a unique group for a platform, but the group for a platform matching the tag.
         * If no or multiple groups (or more than one group with regionMatch: true)
         * match the tag for a same platform - it is considered this Beta has no eligible group for this platform.
         */
        // We need to define one eligible group per platform, so grouping them for further processing
        const groupsByPlatform = groupBy(groups, (group) => group.PlatformId);

        Object.entries(groupsByPlatform).forEach(([betaPlatformId, responsePlatformGroups]) => {
            // Transform Beta API ids into internal Sitegen platformId
            const platformId = BETA_PLATFORM_ID_TO_PLATFORM[betaPlatformId];
            let platformGroups: IPlatformPlayerGroupResponse[] = responsePlatformGroups;
            let isOnlyOneGroup = platformGroups.length === 1;

            if (widgetName && widgetName === WIDGET_NAME.BETA_APPROVAL && isOnlyOneGroup) {
                return platforms[platformId] = platformGroups[0].PlayerGroupId;
            }

            // Filter groups by tag name if provided
            if (!isEmpty(tagFromConfig)) {
                const tagMatchedGroups = responsePlatformGroups.filter((group) => {
                    return group.Tags && group.Tags.split(',').includes(tagFromConfig as string);
                });
                platformGroups = tagMatchedGroups;
                isOnlyOneGroup = platformGroups.length === 1;
            }
            /**
             * For the "non region match only" platforms, the group is eligible if it is the only
             * available group for the platform
             */
            const isNonRegionMatchOnlyPlatform = !REGION_MATCH_ONLY_PLATFORMS.includes(platformId);
            if (isNonRegionMatchOnlyPlatform) {
                if (isOnlyOneGroup) {
                    return platforms[platformId] = platformGroups[0].PlayerGroupId;
                }
            }
            /**
             * For the case when multiple groups available for the platform,
             * the only single group with regionMatch: true is considered as eligible.
             * In case if several groups has regionMatch: true, it is considered there is no  eligible groups at all.
             */
            const regionMatchedGroups = platformGroups.filter((group) => group.RegionMatch);
            const isOnlyOneRegionMatchedGroup = regionMatchedGroups.length === 1;
            if (isOnlyOneRegionMatchedGroup) {
                return platforms[platformId] = regionMatchedGroups[0].PlayerGroupId;
            }

            return;
        });

        return platforms;
    };

    /** At the moment we consider that response can have only one group */
    private static getNDAStatus(response: IGetPlayerProfileResponse): NDA_STATUS {
        const group: IPlayerGroupResponse = response.PlayerGroups[0];
        switch (group?.PlayerSystemInfo?.Components?.NDA) {
        case 'true':
            return NDA_STATUS.SIGNED;
        case 'false':
            return NDA_STATUS.NOT_SIGNED;
        case '':
        default:
            return NDA_STATUS.NOT_SPECIFIED;
        }
    }

    private static getCTA(response: IGetPlayerProfileResponse): string | null {
        const group: IPlayerGroupResponse = response.PlayerGroups[0];
        return group?.PlayerSystemInfo?.Components?.CTA ?? null;
    }

    /** Convert Beta API error to application-specific error */
    private static getBetaErrorFromResponse(error: AxiosError): IBetaError {
        const result: IBetaAPIError = error.response!.data as IBetaAPIError;
        return {
            code: result.ErrorCode,
            description: result.ErrorDescription,
            message: result.ErrorMessage
        };
    }

    private static request<T>(options: AxiosRequestConfig): Promise<T> {
        return this.AXIOS_INSTANCE.request({ ...options, responseType: 'json' })
            .then((response: AxiosResponse): T => {
                return response.data;
            }).catch((e: AxiosError): never => {
                throw this.getBetaErrorFromResponse(e);
            });
    }
}
